import { cn } from "../../lib/utils";
import { ReactNode, CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useReducedMotion } from "motion/react";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import {
  TYPEWRITER_SPEED,
  useTypewriter,
} from "../../domains/gpt/hooks/useTypewriter";

// The app header line, borrowed from `Header` so JuisciGPT's burger sits
// exactly where every other page puts its title and avatar. Shared by the
// landing header and the boot skeleton — the same line at two moments, where
// the burger visibly jumps if either drifts — and by the history panel's
// identity row, which reuses the geometry but deliberately rides lower (see
// GPTHistoryPanel).
//
// `Header` builds the line from two boxes — `px-5 py-3` around a `min-h-12`
// row — which puts its content's centre 36px below the top. Collapsed into one
// box the same classes give 30px instead, because `min-height` counts the
// padding under `border-box`. `min-h-18` is that 12 + 48 + 12 as a single
// value, so one element reproduces the two-box geometry exactly.
//
// The safe-area inset is NOT included: it belongs to the box above (the
// ScrollableLayout header slot on the landing, the panel/skeleton root
// elsewhere). Folding it in here would override this row's own top padding.
export const GPT_HEADER_ROW = "flex min-h-18 items-center px-5 py-3";

// The desktop reading column. Full-bleed on mobile; from `md` up every band of
// the chat surface (the turns, the sticky header row, the sources list) is
// capped to this same centered width, so a long answer reads as a column
// instead of stretching across the whole desktop content area.
export const GPT_DESKTOP_COLUMN = "md:mx-auto md:w-full md:max-w-gpt-column";

// The composer band, wider than the reading column it sits under (Figma).
export const GPT_DESKTOP_COMPOSER =
  "md:mx-auto md:w-full md:max-w-gpt-composer";

// The page behind JuisciGPT: the desktop canvas every desktop page sits on
// (Figma "color/base/canvas"), the cream of the phone app.
export function gptBackground():
  "var(--color-ds-canvas)" | "var(--color-ds-cream)" {
  return isDesktopShell() ? "var(--color-ds-canvas)" : "var(--color-ds-cream)";
}

// Under the desktop composer (Figma "mdr-line"): what JuisciGPT is for, on
// every desktop surface that asks it a question. The phone keeps its
// end-of-scroll disclaimer instead.
// How long a widget takes to slide into the composer band.
export const GPT_BAND_ENTRANCE_SECONDS = 0.22;

// `typed` follows a button that slides up into a read-only band: the notice
// types itself once the button has landed. Its full width is held from the
// first frame, so the centred line does not drift while it grows.
export function GptDesktopNotice({
  reveal,
}: {
  reveal: "static" | "typed";
}): React.JSX.Element {
  const { t } = useTranslation();
  const notice = t(
    "JuisciGPT · For non-urgent use · Does not decide for an individual patient"
  );
  const prefersReducedMotion = useReducedMotion();
  const typed = useTypewriter(
    notice,
    reveal === "typed" && prefersReducedMotion !== true,
    TYPEWRITER_SPEED.notice,
    GPT_BAND_ENTRANCE_SECONDS * 1000
  );
  return (
    <p className="m-0 text-center font-dm-sans text-footnote text-ds-slate-700">
      <span className="sr-only">{notice}</span>
      <span aria-hidden>{typed.text}</span>
      <span aria-hidden className="invisible">
        {notice.slice(typed.text.length)}
      </span>
    </p>
  );
}

export function GPTConversationWrapper({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "flex-1 p-[32px_0_calc(64px+var(--safe-area-bottom))] flex flex-col gap-6",
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}
